import moment from 'moment';
import HelperMixin from '../../Shared/HelperMixin.vue';
import PaginationHelper from '../../Shared/PaginationHelper.vue';

const STATUS_PENDING = 2;
const STATUS_FROZEN = 3;
const STATUS_ROUTED = 4;
const STATUS_COMPLETED = 5;
const STATUS_REJECTED = 6;
const STATUS_CANCELLED = 7;

const STATUS_OPTIONS = [
    { value: 0, label: 'كل الحالات' },
    { value: STATUS_PENDING, label: 'قيد المراجعة' },
    { value: STATUS_FROZEN, label: 'معتمد ومجمَّد' },
    { value: STATUS_ROUTED, label: 'موجَّه لمرفق مستفيد' },
    { value: STATUS_COMPLETED, label: 'مكتمل' },
    { value: STATUS_REJECTED, label: 'مرفوض' },
    { value: STATUS_CANCELLED, label: 'ملغى' },
];

export default {
    name: 'RecycleReview',
    mixins: [HelperMixin],
    components: { PaginationHelper },

    async created() {
        await this.CheckLoginStatus();
        if (!this.loginDetails || !this.canViewPage) {
            this.$helper.ShowMessage('error', 'غير مصرح', 'مراجعة طلبات إعادة التدوير متاحة لإدارة الصيدلة والوزارة.');
            this.href('/Home');
            return;
        }
        this.GetInfo(1);
    },

    computed: {
        canViewPage() {
            return !!(this.loginDetails && this.hasInventoryAndStockAccessUi());
        },
        statusOptions() {
            return STATUS_OPTIONS;
        },
        /** إجمالي ما سيُجمَّد فعلياً حسب المتاح الآن (لا حسب المطلوب). */
        approveEffectiveTotal() {
            if (!this.detail) return 0;
            return (this.detail.lines || [])
                .filter((l) => l.lineStatus === 1)
                .reduce((s, l) => s + Number(l.effectiveQuantity || 0), 0);
        },
        shortfallLines() {
            if (!this.detail) return [];
            return (this.detail.lines || []).filter((l) => l.lineStatus === 1 && l.hasShortfall);
        },

        /** المرشحون غير المضافين بعد، بعد البحث والفلتر — نفس منطق شاشة المرفق. */
        availableCandidates() {
            const used = new Set((this.nominateForm.lines || []).map((l) => l.sourceProductsCountId));
            let list = this.candidates.filter((c) => !used.has(c.sourceProductsCountId));

            if (this.candidateFilter === 'expiry') list = list.filter((c) => c.reason === 2);
            else if (this.candidateFilter === 'idle') list = list.filter((c) => c.reason === 1);
            else if (this.candidateFilter === 'surplus') list = list.filter((c) => c.reason === 3);

            const term = String(this.candidateSearch || '').trim().toLowerCase();
            if (term) {
                list = list.filter((c) => [c.productName, c.scientificName, c.nationalCode, c.batchNumber]
                    .some((v) => String(v || '').toLowerCase().includes(term)));
            }
            return list;
        },

        candidateCounts() {
            const used = new Set((this.nominateForm.lines || []).map((l) => l.sourceProductsCountId));
            const list = this.candidates.filter((c) => !used.has(c.sourceProductsCountId));
            return {
                all: list.length,
                expiry: list.filter((c) => c.reason === 2).length,
                idle: list.filter((c) => c.reason === 1).length,
                surplus: list.filter((c) => c.reason === 3).length,
            };
        },

        nominateTotalQty() {
            return (this.nominateForm.lines || []).reduce((s, l) => s + Number(l.quantity || 0), 0);
        },
    },

    filters: {
        moment(date) {
            if (!date) return '—';
            return moment(date).format('DD/MM/YYYY HH:mm');
        },
        momentDate(date) {
            if (!date) return '—';
            return moment(date).format('DD/MM/YYYY');
        },
    },

    data() {
        return {
            filters: { pageNo: 1, pageSize: 25, status: 0, search: '' },
            Info: [],
            pages: 0,

            detail: null,
            detailDialog: false,

            approveDialog: false,
            approveForm: { note: '', beneficiaryBranchesId: null },

            routeDialog: false,
            routeForm: { beneficiaryBranchesId: null, note: '' },

            beneficiaries: [],
            loadingBeneficiaries: false,

            // ترشيح نيابةً عن مرفق (قرار العميل 2026-09-22)
            nominateDialog: false,
            nominateForm: { branchesId: null, note: null, lines: [] },
            branches: [],
            loadingBranches: false,
            candidates: [],
            candidatesMeta: { stagnantDays: 90, nearExpiryDays: 90 },
            loadingCandidates: false,
            candidateFilter: 'all',
            candidateSearch: '',

            availabilityDialog: false,
            availability: null,
            loadingAvailability: false,
        };
    },

    methods: {
        statusLabel(s) {
            const found = STATUS_OPTIONS.find((x) => x.value === s);
            return found ? found.label : '—';
        },
        statusBadge(s) {
            if (s === STATUS_COMPLETED) return 'success';
            if (s === STATUS_FROZEN || s === STATUS_ROUTED) return 'primary';
            if (s === STATUS_PENDING) return 'warning';
            if (s === STATUS_REJECTED || s === STATUS_CANCELLED) return 'danger';
            return 'secondary';
        },
        statusBadgeClass(badge) {
            return 'recycle-badge recycle-badge--' + (badge || 'secondary');
        },
        reasonBadge(reason) {
            return reason === 2 ? 'danger' : (reason === 1 ? 'warning' : 'secondary');
        },

        apiError(err, fallback) {
            const msg = err && err.response && err.response.data ? err.response.data : fallback;
            this.$helper.ShowMessage('error', 'خطأ', msg);
        },

        // ─── القائمة ─────────────────────────────────────────────────────
        GetInfo(pageNo) {
            this.filters.pageNo = pageNo || 1;
            this.$blockUI.Start();
            this.$http.GetRecycleRequests(this.filters)
                .then((res) => {
                    this.$blockUI.Stop();
                    this.Info = (res && res.data && res.data.info) || [];
                    this.pages = (res && res.data && res.data.count) || 0;
                })
                .catch(() => {
                    this.$blockUI.Stop();
                    this.Info = [];
                    this.pages = 0;
                });
        },

        ApplyFilters() {
            this.GetInfo(1);
        },

        ResetFilters() {
            this.filters.status = 0;
            this.filters.search = '';
            this.ApplyFilters();
        },

        // ─── التفاصيل (المقارنة الحية) ───────────────────────────────────
        openDetail(row) {
            if (!row || !row.id) return;
            this.$blockUI.Start();
            this.$http.GetRecycleRequestById(row.id)
                .then((res) => {
                    this.$blockUI.Stop();
                    this.detail = (res && res.data && res.data.info) || null;
                    this.detailDialog = !!this.detail;
                })
                .catch((err) => {
                    this.$blockUI.Stop();
                    this.apiError(err, 'تعذّر تحميل تفاصيل الطلب.');
                });
        },

        afterAction(message) {
            this.$helper.ShowMessage('success', 'تم', message);
            this.detailDialog = false;
            this.approveDialog = false;
            this.routeDialog = false;
            this.GetInfo(this.filters.pageNo);
        },

        /* ── ترشيح نيابةً عن مرفق ──────────────────────────────────── */

        openNominate() {
            this.nominateForm = { branchesId: null, note: null, lines: [] };
            this.candidates = [];
            this.candidateFilter = 'all';
            this.candidateSearch = '';
            this.nominateDialog = true;

            if (!this.branches.length) {
                this.loadingBranches = true;
                this.$http.GetAllBranches()
                    .then((res) => {
                        this.loadingBranches = false;
                        this.branches = (res && res.data && res.data.info) || [];
                    })
                    .catch(() => {
                        this.loadingBranches = false;
                        this.branches = [];
                    });
            }
        },

        /** تغيير المرفق يُفرغ الأسطر: الدفعات تخص مخزن المرفق السابق ولا تصلح لغيره. */
        onNominateBranchChanged() {
            this.nominateForm.lines = [];
            this.candidates = [];
            this.loadNominateCandidates();
        },

        loadNominateCandidates() {
            const branchId = Number(this.nominateForm.branchesId || 0);
            if (!branchId) { this.candidates = []; return Promise.resolve(); }

            this.loadingCandidates = true;
            return this.$http.GetRecycleCandidates({ branchId })
                .then((res) => {
                    this.loadingCandidates = false;
                    const d = (res && res.data) || {};
                    this.candidates = d.info || [];
                    this.candidatesMeta = {
                        stagnantDays: d.stagnantDays || 90,
                        nearExpiryDays: d.nearExpiryDays || 90,
                    };
                })
                .catch((err) => {
                    this.loadingCandidates = false;
                    this.candidates = [];
                    this.apiError(err, 'تعذّر تحميل أصناف المرفق.');
                });
        },

        addNominateCandidate(c) {
            this.nominateForm.lines.push({
                sourceProductsCountId: c.sourceProductsCountId,
                productName: c.productName,
                batchNumber: c.batchNumber,
                expiryDate: c.expiryDate,
                availableNow: c.quantity,
                quantity: c.quantity,
                reason: c.reason,
                note: '',
            });
        },

        removeNominateLine(index) {
            this.nominateForm.lines.splice(index, 1);
        },

        /**
         * saveOnly=false يرسل الطلب مباشرةً لمراجعة الصيدلة.
         * في الحالتين يُشعَر المرفق: الحجز يبدأ من الترشيح لا من الإرسال.
         */
        submitNominate(saveOnly) {
            const branchId = Number(this.nominateForm.branchesId || 0);
            if (!branchId) {
                this.$helper.ShowMessage('warning', 'تنبيه', 'اختر المرفق الذي تُرشّح من مخزونه.');
                return;
            }

            const lines = (this.nominateForm.lines || [])
                .filter((l) => Number(l.quantity) > 0)
                .map((l) => ({
                    sourceProductsCountId: l.sourceProductsCountId,
                    quantity: Number(l.quantity),
                    reason: l.reason,
                    note: l.note || null,
                }));

            if (!lines.length) {
                this.$helper.ShowMessage('warning', 'تنبيه', 'أضف صنفاً واحداً على الأقل بكمية صحيحة.');
                return;
            }

            const over = (this.nominateForm.lines || [])
                .find((l) => Number(l.quantity) > Number(l.availableNow || 0));
            if (over) {
                this.$helper.ShowMessage('warning', 'تنبيه',
                    `الكمية المطلوبة للصنف ${over.productName} تتجاوز المتاح (${over.availableNow}).`);
                return;
            }

            this.$blockUI.Start();
            this.$http.CreateRecycleRequest({
                branchesId: branchId,
                note: this.nominateForm.note || null,
                lines,
            }, saveOnly)
                .then((res) => {
                    this.$blockUI.Stop();
                    this.nominateDialog = false;
                    this.$helper.ShowMessage('success', 'تم', (res.data && res.data.message) || 'تم الترشيح.');
                    this.GetInfo(1);
                })
                .catch((err) => {
                    this.$blockUI.Stop();
                    this.apiError(err, 'تعذّر إنشاء الترشيح.');
                    // الرفض غالباً بسبب رصيد تغيّر — نُحدّث المعروض.
                    this.loadNominateCandidates();
                });
        },

        openAvailability(productsId) {
            if (!productsId) return;
            this.availability = null;
            this.loadingAvailability = true;
            this.availabilityDialog = true;
            this.$http.GetRecycleAvailability(productsId)
                .then((res) => {
                    this.loadingAvailability = false;
                    this.availability = (res && res.data) || null;
                })
                .catch(() => {
                    this.loadingAvailability = false;
                    this.availability = null;
                });
        },

        /** نص الخيار المختصر — يظهر بعد الاختيار حيث لا يُعرض القالب. */
        beneficiaryLabel(b) {
            if (!b) return '';
            const lack = Number(b.productsOutOfStock || 0);
            const total = (b.perProduct || []).length;
            return lack
                ? `${b.name} — ينقصه ${lack} من ${total} من أصناف الطلب`
                : `${b.name} — لديه كل أصناف الطلب (${b.currentStockOfRequestedProducts})`;
        },

        loadBeneficiaries(requestId) {
            this.loadingBeneficiaries = true;
            return this.$http.GetRecycleBeneficiaries(requestId)
                .then((res) => {
                    this.loadingBeneficiaries = false;
                    this.beneficiaries = (res && res.data && res.data.info) || [];
                })
                .catch(() => {
                    this.loadingBeneficiaries = false;
                    this.beneficiaries = [];
                });
        },

        // ─── الموافقة والتجميد ───────────────────────────────────────────
        openApprove() {
            if (!this.detail) return;
            this.approveForm = { note: '', beneficiaryBranchesId: null };
            this.approveDialog = true;
            this.loadBeneficiaries(this.detail.id);
        },

        submitApprove() {
            this.$blockUI.Start();
            this.$http.ApproveRecycleRequest(this.detail.id, {
                Note: this.approveForm.note || null,
                BeneficiaryBranchesId: this.approveForm.beneficiaryBranchesId || null,
            })
                .then((res) => {
                    this.$blockUI.Stop();
                    this.afterAction((res.data && res.data.message) || 'تم اعتماد الطلب وتجميد الكميات.');
                })
                .catch((err) => {
                    this.$blockUI.Stop();
                    this.apiError(err, 'تعذّر اعتماد الطلب.');
                });
        },

        // ─── الرفض ───────────────────────────────────────────────────────
        rejectRequest() {
            this.$prompt('سبب الرفض (إلزامي)', 'رفض الطلب', {
                confirmButtonText: 'رفض',
                cancelButtonText: 'تراجع',
                inputType: 'textarea',
                inputValidator: (v) => (v && v.trim() ? true : 'السبب إلزامي'),
            }).then(({ value }) => {
                this.$blockUI.Start();
                this.$http.RejectRecycleRequest(this.detail.id, { Reason: value.trim() })
                    .then((res) => {
                        this.$blockUI.Stop();
                        this.afterAction((res.data && res.data.message) || 'تم رفض الطلب.');
                    })
                    .catch((err) => {
                        this.$blockUI.Stop();
                        this.apiError(err, 'تعذّر رفض الطلب.');
                    });
            }).catch(() => { });
        },

        // ─── التوجيه لمرفق مستفيد ────────────────────────────────────────
        openRoute() {
            if (!this.detail) return;
            this.routeForm = { beneficiaryBranchesId: this.detail.beneficiaryBranchesId || null, note: '' };
            this.routeDialog = true;
            this.loadBeneficiaries(this.detail.id);
        },

        submitRoute() {
            if (!this.routeForm.beneficiaryBranchesId) {
                this.$helper.ShowMessage('warning', 'تنبيه', 'اختر المرفق المستفيد.');
                return;
            }
            this.$blockUI.Start();
            this.$http.RouteRecycleRequest(this.detail.id, {
                BeneficiaryBranchesId: Number(this.routeForm.beneficiaryBranchesId),
                Note: this.routeForm.note || null,
            })
                .then((res) => {
                    this.$blockUI.Stop();
                    this.afterAction((res.data && res.data.message) || 'تم تحديد المرفق المستفيد.');
                })
                .catch((err) => {
                    this.$blockUI.Stop();
                    this.apiError(err, 'تعذّر التوجيه.');
                });
        },

        // ─── فك الحجز ────────────────────────────────────────────────────
        releaseRequest() {
            this.$prompt(
                'سيُفك الحجز وتُعاد الكميات إلى رصيد المرفق المرسل. سبب فك الحجز (إلزامي):',
                'فك الحجز',
                {
                    confirmButtonText: 'فك الحجز',
                    cancelButtonText: 'تراجع',
                    inputType: 'textarea',
                    inputValidator: (v) => (v && v.trim() ? true : 'السبب إلزامي'),
                }
            ).then(({ value }) => {
                this.$blockUI.Start();
                this.$http.ReleaseRecycleRequest(this.detail.id, { Reason: value.trim() })
                    .then((res) => {
                        this.$blockUI.Stop();
                        this.afterAction((res.data && res.data.message) || 'تم فك الحجز.');
                    })
                    .catch((err) => {
                        this.$blockUI.Stop();
                        this.apiError(err, 'تعذّر فك الحجز.');
                    });
            }).catch(() => { });
        },

        // ─── تسجيل التسليم الفعلي ────────────────────────────────────────
        markDelivered() {
            this.$prompt('ملاحظة التسليم (اختياري)', 'تسجيل التسليم الفعلي', {
                confirmButtonText: 'تسجيل',
                cancelButtonText: 'تراجع',
                inputType: 'textarea',
            }).then(({ value }) => {
                this.$blockUI.Start();
                this.$http.MarkRecycleDelivered(this.detail.id, { Note: value || null })
                    .then((res) => {
                        this.$blockUI.Stop();
                        this.afterAction((res.data && res.data.message) || 'تم تسجيل التسليم.');
                    })
                    .catch((err) => {
                        this.$blockUI.Stop();
                        this.apiError(err, 'تعذّر تسجيل التسليم.');
                    });
            }).catch(() => { });
        },
    },
};
